import { z } from 'zod';
import {
  ASSIGNMENT_TARGET_PLATFORMS,
  COURSE_STATUSES,
  ENROLLMENT_ROLES,
  MODULE_ITEM_TYPES,
} from '../enums';
import { slugSchema, timestampMsSchema, uuidSchema } from './common';

/* ─── Course shapes ─────────────────────────────────────────────────────── */

export const courseShape = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  /** The org's slug, denormalized into the course shape so URL routing
   *  (`/c/:orgSlug/:courseSlug`) doesn't need a second fetch. */
  orgSlug: slugSchema,
  creatorProfileId: uuidSchema,
  slug: slugSchema,
  title: z.string().min(1).max(160),
  description: z.string().nullable(),
  status: z.enum(COURSE_STATUSES),
  joinCode: z.string().min(4).max(12),
  heroImageR2Key: z.string().nullable(),
  targetPlatform: z.enum(ASSIGNMENT_TARGET_PLATFORMS),
  /** Whether this course shows in the public Template Library
   *  (`/templates`). Migration 0011. Optional for backward compat. */
  publishedAsTemplate: z.boolean().optional(),
  createdAt: timestampMsSchema,
  updatedAt: timestampMsSchema,
});
export type CourseShape = z.infer<typeof courseShape>;

export const createCourseRequest = z.object({
  slug: slugSchema,
  title: z.string().min(1).max(160),
  description: z.string().max(2000).optional(),
});
export type CreateCourseRequest = z.infer<typeof createCourseRequest>;

export const updateCourseRequest = z.object({
  title: z.string().min(1).max(160).optional(),
  description: z.string().max(2000).nullable().optional(),
  status: z.enum(COURSE_STATUSES).optional(),
  heroImageR2Key: z.string().nullable().optional(),
  /** Toggle the course into / out of the public Template Library
   *  (`/templates`). Migration 0011. */
  publishedAsTemplate: z.boolean().optional(),
});
export type UpdateCourseRequest = z.infer<typeof updateCourseRequest>;

export const listCoursesResponse = z.object({
  courses: z.array(courseShape),
});
export type ListCoursesResponse = z.infer<typeof listCoursesResponse>;

/* ─── Module shapes ─────────────────────────────────────────────────────── */

export const moduleShape = z.object({
  id: uuidSchema,
  courseId: uuidSchema,
  parentModuleId: uuidSchema.nullable(),
  title: z.string().min(1).max(160),
  description: z.string().nullable(),
  position: z.number().int(),
  createdAt: timestampMsSchema,
  updatedAt: timestampMsSchema,
});
export type ModuleShape = z.infer<typeof moduleShape>;

/* ─── Module-item shapes ────────────────────────────────────────────────── */

export const moduleItemShape = z.object({
  id: uuidSchema,
  moduleId: uuidSchema,
  type: z.enum(MODULE_ITEM_TYPES),
  title: z.string().min(1).max(200),
  position: z.number().int(),
  publishAt: timestampMsSchema.nullable(),
  unpublishAt: timestampMsSchema.nullable(),
  payloadJson: z.string().nullable(),
  createdAt: timestampMsSchema,
  updatedAt: timestampMsSchema,
});
export type ModuleItemShape = z.infer<typeof moduleItemShape>;

/* ─── Enrollment ────────────────────────────────────────────────────────── */

export const courseEnrollmentShape = z.object({
  id: uuidSchema,
  courseId: uuidSchema,
  profileId: uuidSchema,
  role: z.enum(ENROLLMENT_ROLES),
  firstSeenAt: timestampMsSchema.nullable(),
  lastSeenAt: timestampMsSchema.nullable(),
  createdAt: timestampMsSchema,
});
export type CourseEnrollmentShape = z.infer<typeof courseEnrollmentShape>;

/** Student joining via 6-char code typed on /join. */
export const joinCourseRequest = z.object({
  joinCode: z.string().min(4).max(12),
});
export type JoinCourseRequest = z.infer<typeof joinCourseRequest>;
